import {
  AutomaticApplySkipReason,
  describeError,
  IAutomaticApplySkip,
} from "./automaticApplySkipReason";

const BEACON_FALLBACK_TIMEOUT_MS = 400;

/**
 * The body is still JSON, but it is labelled text/plain because that is a
 * CORS-safelisted content type, so the browser sends the request straight away
 * with no OPTIONS preflight. A preflighted beacon is not safe here: the
 * hand-off navigates away immediately, and the POST is lost whenever the
 * preflight has not come back by then. The API parses text/plain JSON on the
 * beacon routes only - see beacon-body-parser.util.ts in apps/backend.
 *
 * Do not change this to application/json or add request headers; either one
 * brings the preflight back.
 */
const BEACON_CONTENT_TYPE = "text/plain;charset=UTF-8";

export interface IAutomaticApplyHandoffBeacon {
  url: string;
  payload: Record<string, unknown>;
}

/**
 * Hands one payload to the browser's background delivery queue.
 *
 * Returns true when the browser has taken ownership of the request. Because
 * the request is CORS-simple it needs no preflight, so the browser sends it
 * even though the page is about to be replaced.
 */
export function queueBeacon({
  url,
  payload,
}: IAutomaticApplyHandoffBeacon): boolean {
  if (typeof navigator === "undefined" || !navigator.sendBeacon) {
    return false;
  }
  try {
    const blob = new Blob([JSON.stringify(payload)], {
      type: BEACON_CONTENT_TYPE,
    });
    return navigator.sendBeacon(url, blob);
  } catch {
    return false;
  }
}

/**
 * Used only when sendBeacon is unavailable or refuses the payload. `keepalive`
 * lets the request outlive the page, and the same text/plain content type keeps
 * it free of a preflight. The caller races it against a short timeout so a slow
 * network cannot hold the candidate on the interstitial.
 */
async function sendWithKeepalive({
  url,
  payload,
}: IAutomaticApplyHandoffBeacon): Promise<void> {
  try {
    await fetch(url, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": BEACON_CONTENT_TYPE },
      body: JSON.stringify(payload),
    });
  } catch {
    // The candidate is being handed off regardless; a lost analytics row must
    // never block the journey.
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Emits every event the hand-off records - the job view as well as the apply
 * click - then resolves as soon as delivery is assured. Anything sent through
 * the ordinary API client instead is cancelled by the navigation that follows.
 *
 * When the browser accepts all of them, that is immediate - waiting longer buys
 * nothing, because an accepted beacon outlives the page. Otherwise we fall back
 * to keepalive requests and give them at most BEACON_FALLBACK_TIMEOUT_MS.
 */
export async function emitHandoffEvents(
  beacons: IAutomaticApplyHandoffBeacon[]
): Promise<void> {
  const rejected = beacons.filter((beacon) => !queueBeacon(beacon));
  if (rejected.length === 0) {
    return;
  }
  await Promise.race([
    Promise.all(rejected.map(sendWithKeepalive)),
    delay(BEACON_FALLBACK_TIMEOUT_MS),
  ]);
}

/**
 * Replaces the current history entry rather than pushing a new one, so the job
 * page is not left behind. Back from the employer's site returns the candidate
 * to the board they came from instead of bouncing them straight back out.
 */
export function navigateToApplyUrl(applyUrl: string): void {
  window.location.replace(applyUrl);
}

const HANDOFF_MARKER_PREFIX = "mygwork:auto-apply-handoff:";

function handoffMarkerKey(jobId: number): string {
  return `${HANDOFF_MARKER_PREFIX}${jobId}`;
}

/**
 * A hand-off happens once per job per session. Coming back and reopening the
 * same job must give the candidate the normal page, otherwise pressing back
 * would throw them straight out to the employer again.
 *
 * Returns why the hand-off is suppressed, or undefined when it is not. Every
 * access is guarded: Safari in private mode throws on sessionStorage, and that
 * must degrade to showing the normal page rather than breaking it - recorded
 * separately from a genuine repeat visit so the two can be told apart.
 */
export function getHandoffSessionSuppression(
  jobId: number
): IAutomaticApplySkip | undefined {
  try {
    if (typeof window === "undefined") {
      return undefined;
    }
    return window.sessionStorage.getItem(handoffMarkerKey(jobId)) !== null
      ? { reason: AutomaticApplySkipReason.ALREADY_HANDED_OFF }
      : undefined;
  } catch (error) {
    return {
      reason: AutomaticApplySkipReason.SESSION_STORAGE_UNAVAILABLE,
      detail: describeError(error),
    };
  }
}

export function markHandedOffThisSession(jobId: number): void {
  try {
    window.sessionStorage.setItem(handoffMarkerKey(jobId), "true");
  } catch {
    // Nothing to do - worst case the candidate is handed off again on a repeat
    // visit, which is better than blocking this one.
  }
}
